import {
  findShippingRegion,
  type IssuedQuoteVersion,
  type OrderPlacement as OrderPlacementData,
  type OrderWithdrawalNotice,
  type PlaceOrderInput,
  placeOrderInputSchema,
  type ShippingAddress,
} from '@repo/api-contracts';
import { useId, useRef, useState } from 'react';
import {
  DESTINATION_LIST_MESSAGE,
  destinationFieldMessage,
  destinationIssueTarget,
} from '@/entities/quote';
import {
  formatKrw,
  getServerFieldErrors,
  orderPlacementMessage,
  SHIPPING_REGION_CHANGED_MESSAGE,
} from '@/shared/lib';
import { Button, FormSection } from '@/shared/ui';
import { PlacementAmountSummary } from './placement-amount-summary';
import { PlacementConsent } from './placement-consent';
import { PlacementDestinationCard } from './placement-destination-card';

type PlacementDestination = OrderPlacementData['destinations'][number];
type AddressKey = keyof ShippingAddress;
type AddressErrors = Partial<Record<AddressKey, string>>;

interface Props {
  quoteRequestId: string;
  /** 발행본 — 요약(합계·납기·공급 방법)과 거래 조건(공통 문구)을 보여 준다. */
  version: IssuedQuoteVersion;
  /** 별첨 고지 원문(orders.getPlacement) — 두 번째 동의가 가리키는 문서다. */
  withdrawalNotice: OrderWithdrawalNotice;
  /** 견적 배송지(sortOrder 순) — 카드 하나씩. 비면(배송지 없는 견적) 주문할 수 없다(교차 조정 R15). */
  destinations: OrderPlacementData['destinations'];
  /** 다시 발행돼 다시 그릴 때 마지막으로 제출한 주소를 견적 배송지 id 로 맞춰 이어 쓴다. */
  previousInput?: PlaceOrderInput | null;
  onSubmit: (input: PlaceOrderInput) => Promise<void>;
  pending?: boolean;
  error?: string | null;
}

/** 비회원으로 요청한 견적 배송지가 있을 때 — 비회원은 요청 때 받는 분·연락처·상세 주소를 받지 않는다(spec D8). */
const GUEST_ORIGIN_HINT =
  '견적을 요청할 때는 받는 분·연락처·상세 주소를 받지 않았어요. 배송지마다 입력해 주세요.';
/** 배송지 없는 견적 — 서버도 `INVALID_DESTINATIONS` 로 거절한다(교차 조정 R15). */
const NO_DESTINATION_NOTICE = '배송지 정보가 없는 견적이에요. 고객센터로 문의해 주세요.';
const TERMS_CONSENT = '이용약관·청약철회 제한 사항·배송비 등 비용 부담 확인';
const WITHDRAWAL_CONSENT = '주문 제작 상품 청약철회 제한 별도 고지 확인';
const ADDRESS_KEYS: readonly AddressKey[] = [
  'recipientName',
  'recipientPhone',
  'postalCode',
  'address1',
  'address2',
];

interface CardState {
  readonly quote: PlacementDestination;
  readonly address: ShippingAddress;
}

/** 견적 배송지 주소 → 입력 칸 시작 값(비회원 출처의 빈 칸은 빈 문자열). */
const toAddress = (address: PlacementDestination['address']): ShippingAddress => ({
  recipientName: address.recipientName ?? '',
  recipientPhone: address.recipientPhone ?? '',
  postalCode: address.postalCode,
  address1: address.address1,
  address2: address.address2 ?? '',
});

const previousAddressOf = (
  previous: PlaceOrderInput | null,
  quoteDestinationId: string,
): ShippingAddress | null => {
  const submitted = previous?.destinations.find(
    (item) => item.quoteDestinationId === quoteDestinationId,
  );
  if (submitted === undefined) return null;
  const { recipientName, recipientPhone, postalCode, address1, address2 } = submitted;
  return { recipientName, recipientPhone, postalCode, address1, address2 };
};

const isAddressKey = (key: string): key is AddressKey =>
  (ADDRESS_KEYS as readonly string[]).includes(key);

/**
 * 지역 사전 점검(spec D10) — 견적 배송지의 지역을 아는 카드만(지역을 모르면 제한 없음, R1). 우편번호 형식 오류는 계약 검증이 맡는다.
 * 서버(`assertSameShippingRegion`)와 같은 판단이라 바뀌면 보내지 않고 그 카드 우편번호에 안내를 단다.
 */
const regionChanged = (card: CardState): boolean => {
  if (card.quote.region === null) return false;
  const region = findShippingRegion(card.address.postalCode.trim());
  return region !== null && region !== card.quote.region;
};

const addCardError = (
  errors: Record<number, AddressErrors>,
  index: number,
  key: AddressKey,
  message: string,
): void => {
  const card = errors[index] ?? {};
  if (card[key] === undefined) errors[index] = { ...card, [key]: message };
};

/**
 * 주문하기 폼 — 상품 상세·견적 요청과 같은 두 칸(lg). 왼쪽은 번호 붙은 묶음(견적 내용 → 배송지 카드 → 확인 및 동의),
 * 오른쪽은 따라 내려오는(sticky) 주문 합계·안내·주문하기 버튼. 좁은 화면에서는 묶음 다음에 합계·버튼이 온다.
 * 배송지는 견적 배송지마다 카드 하나(SUPER-86 spec §5-3) — 수량·배송비는 읽기 전용, 주소만 고치며 지역 구분은 바꿀 수 없다.
 * 두 동의를 모두 받기 전에는 주문하기를 잠그고 그 이유를 버튼 설명(aria-describedby)으로 단다.
 */
export function OrderPlacement({
  quoteRequestId,
  version,
  withdrawalNotice,
  destinations,
  previousInput = null,
  onSubmit,
  pending = false,
  error,
}: Props) {
  const [cards, setCards] = useState<readonly CardState[]>(() =>
    destinations.map((quote) => ({
      quote,
      address:
        previousAddressOf(previousInput, quote.quoteDestinationId) ?? toAddress(quote.address),
    })),
  );
  /** 카드 위치 → 칸 오류. 카드는 견적 배송지 순서로 고정이라(추가·삭제 없음) 위치가 곧 카드다. */
  const [cardErrors, setCardErrors] = useState<Readonly<Record<number, AddressErrors>>>({});
  const [termsAgreed, setTerms] = useState(false);
  const [withdrawalNoticeAgreed, setWithdrawal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const consentHintId = useId();
  const bothAgreed = termsAgreed && withdrawalNoticeAgreed;
  const locked = pending || saving;
  const orderable = cards.length > 0;
  const hasGuestOrigin = destinations.some((item) => item.address.recipientName === null);

  function changeCard(index: number, patch: Partial<ShippingAddress>) {
    setCards((previous) =>
      previous.map((card, position) =>
        position === index ? { ...card, address: { ...card.address, ...patch } } : card,
      ),
    );
    setCardErrors((previous) => {
      const current = previous[index];
      if (current === undefined) return previous;
      const next = { ...current };
      for (const key of Object.keys(patch)) if (isAddressKey(key)) delete next[key];
      return { ...previous, [index]: next };
    });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (pending || lock.current || !orderable) return;
    const errors: Record<number, AddressErrors> = {};
    cards.forEach((card, index) => {
      if (regionChanged(card))
        addCardError(errors, index, 'postalCode', SHIPPING_REGION_CHANGED_MESSAGE);
    });
    const parsed = placeOrderInputSchema.safeParse({
      quoteRequestId,
      versionId: version.id,
      destinations: cards.map((card) => ({
        ...card.address,
        quoteDestinationId: card.quote.quoteDestinationId,
      })),
      termsAgreed,
      withdrawalNoticeAgreed,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const target = destinationIssueTarget(issue.path);
        const card = target === null ? undefined : cards[target.index];
        if (target === null || card === undefined || !isAddressKey(target.key)) continue;
        addCardError(
          errors,
          target.index,
          target.key,
          destinationFieldMessage(target.key, issue.code, card.address[target.key]),
        );
      }
    }
    setCardErrors(errors);
    if (Object.keys(errors).length > 0) {
      setMessage(DESTINATION_LIST_MESSAGE);
      return;
    }
    if (!parsed.success) {
      setMessage('배송지와 두 동의 항목을 확인해 주세요.');
      return;
    }
    lock.current = true;
    setSaving(true);
    setMessage(null);
    try {
      await onSubmit(parsed.data);
    } catch (failure) {
      setMessage(orderPlacementMessage(failure));
      // 서버 도메인 검사가 가리킨 배송지 칸(`destinations.{i}.{칸}`, 교차 조정 R3)은 그 카드에 서버 문구로 단다.
      const serverErrors: Record<number, AddressErrors> = {};
      for (const [key, messages] of Object.entries(getServerFieldErrors(failure) ?? {})) {
        const target = destinationIssueTarget(key);
        const [first] = messages;
        if (target === null || first === undefined || !isAddressKey(target.key)) continue;
        if (target.index < cards.length)
          addCardError(serverErrors, target.index, target.key, first);
      }
      setCardErrors(serverErrors);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }
  return (
    <form
      onSubmit={submit}
      className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16"
    >
      <div className="flex min-w-0 flex-col gap-12">
        <FormSection order={1} title="견적 내용">
          <section aria-label="발행본 요약" className="flex flex-col gap-5">
            <h3 className="text-lg font-bold tracking-[-0.02em]">발행본 {version.version}</h3>
            <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm wrap-anywhere">
              <div className="flex gap-4 py-3">
                <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">합계</dt>
                <dd className="min-w-0 flex-1 font-semibold tabular-nums">
                  {formatKrw(version.totalAmount)}
                </dd>
              </div>
              <div className="flex gap-4 py-3">
                <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">납기</dt>
                <dd className="min-w-0 flex-1 whitespace-pre-wrap">{version.deliveryNote}</dd>
              </div>
              <div className="flex gap-4 py-3">
                <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">공급 방법</dt>
                <dd className="min-w-0 flex-1 whitespace-pre-wrap">{version.deliveryMethodNote}</dd>
              </div>
            </dl>
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-muted-foreground">거래 조건</p>
              <p className="bg-muted/60 p-5 text-sm leading-relaxed whitespace-pre-wrap wrap-anywhere">
                {version.termsText}
              </p>
            </div>
          </section>
        </FormSection>

        <FormSection order={2} title="배송지">
          {orderable ? null : (
            <p className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm leading-relaxed">
              {NO_DESTINATION_NOTICE}
            </p>
          )}
          {hasGuestOrigin ? (
            <p className="border-l-2 border-brand-lime bg-muted/70 px-4 py-3 text-sm leading-relaxed">
              {GUEST_ORIGIN_HINT}
            </p>
          ) : null}
          {cards.map((card, index) => (
            <PlacementDestinationCard
              key={card.quote.quoteDestinationId}
              order={index + 1}
              quantity={card.quote.quantity}
              sizes={card.quote.sizes}
              shippingFee={card.quote.shippingFee}
              region={card.quote.region}
              value={card.address}
              errors={cardErrors[index]}
              disabled={locked}
              onChange={(patch) => changeCard(index, patch)}
            />
          ))}
        </FormSection>

        <FormSection order={3} title="확인 및 동의">
          <section
            aria-label="청약철회 제한 별도 고지"
            className="flex flex-col gap-3 border-l-2 border-foreground bg-muted/60 px-5 py-4"
          >
            <h3 className="text-sm font-semibold">청약철회 제한 별도 고지</h3>
            <p className="text-sm leading-relaxed whitespace-pre-wrap wrap-anywhere">
              {withdrawalNotice.text}
            </p>
          </section>
          <ul className="flex flex-col divide-y divide-foreground/10 border-y border-foreground/10">
            <PlacementConsent
              label={TERMS_CONSENT}
              checked={termsAgreed}
              disabled={locked}
              onChange={setTerms}
            />
            <PlacementConsent
              label={WITHDRAWAL_CONSENT}
              checked={withdrawalNoticeAgreed}
              disabled={locked}
              onChange={setWithdrawal}
            />
          </ul>
        </FormSection>
      </div>

      <div className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
        <PlacementAmountSummary version={version} />
        {(message || error) && (
          <p
            role="alert"
            className="border-l-2 border-destructive bg-destructive/5 px-5 py-4 text-sm text-destructive"
          >
            {message || error}
          </p>
        )}
        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            isLoading={saving}
            disabled={pending || !bothAgreed || !orderable}
            aria-describedby={bothAgreed ? undefined : consentHintId}
            className="h-14 w-full text-base"
          >
            주문하기
          </Button>
          {bothAgreed ? null : (
            <p id={consentHintId} className="text-center text-xs text-muted-foreground">
              두 동의 항목을 모두 확인하면 주문할 수 있습니다.
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
